<template>
	<div class="space-y-6 sm:space-y-8 px-2 sm:px-0">
		<header class="px-2 sm:px-0">
			<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
				<h1 class="text-2xl sm:text-3xl font-bold">{{ $t('tracking.greeting', { name: username }) }}</h1>
				<!-- Mini Stats -->
				<div v-if="!loading" class="flex gap-3 sm:gap-4">
					<div class="flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
						<i class="pi pi-star-fill text-primary text-sm"></i>
						<span class="text-sm font-semibold text-primary">{{ $t('tracking.miniStats.todaysXp', {
							xp:
								todaysStats.totalXp
						}) }}</span>
					</div>
					<div class="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
						<i class="pi pi-check-circle text-green-600 text-sm"></i>
						<span class="text-sm font-semibold text-green-600">{{ $t('tracking.miniStats.todaysChores', {
							count: todaysStats.choreCount
						}) }}</span>
					</div>
				</div>
				<div v-else class="flex gap-3">
					<Skeleton width="8rem" height="2rem" />
					<Skeleton width="8rem" height="2rem" />
				</div>
			</div>
		</header>

		<!-- Statistics Section (Collapsible) -->
		<Card class="mx-2 sm:mx-0" v-if="!loading">
			<template #content>
				<div class="flex items-center justify-between cursor-pointer" @click="showStatistics = !showStatistics">
					<h2 class="text-lg font-semibold flex items-center gap-2">
						<i class="pi pi-chart-bar text-primary"></i>
						{{ $t('tracking.statistics.title') }}
					</h2>
					<Button :icon="showStatistics ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" text rounded
						:aria-label="showStatistics ? $t('tracking.statistics.toggleHide') : $t('tracking.statistics.toggleShow')" />
				</div>
				<transition name="slide-down">
					<div v-if="showStatistics" class="mt-4">
						<StatisticsSection />
					</div>
				</transition>
			</template>
		</Card>
		<Skeleton v-else width="100%" height="4rem" class="mx-2 sm:mx-0" />

		<template v-if="!loading">

			<!-- Prize Selection Alert -->
			<div class="px-2 sm:px-0">
				<template v-if="!loadingSections.prizeAlert">
					<PrizeMissingSelectionsAlert />
				</template>
				<Skeleton v-else width="100%" height="4rem" />
			</div>

			<!-- Quick Actions -->
			<div class="flex flex-col sm:flex-row gap-2 sm:gap-3 px-2 sm:px-0">
				<template v-if="!loadingSections.quickActions">
					<Button icon="pi pi-plus" :label="$t('createOneTimeTask')" severity="success"
						@click="showOneTimeForm = !showOneTimeForm"
						class="w-full sm:w-auto justify-center text-sm sm:text-base" />
					<Button icon="pi pi-calendar-clock" :label="$t('scheduleTask')" severity="primary"
						@click="showScheduleModal = true"
						class="w-full sm:w-auto justify-center text-sm sm:text-base" />
				</template>
				<template v-else>
					<Skeleton width="100%" height="2.5rem" class="sm:w-48" />
					<Skeleton width="100%" height="2.5rem" class="sm:w-48" />
				</template>
			</div>

			<!-- Search and Sorting Controls -->
			<Card class="mx-2 sm:mx-0">
				<template #content>
					<template v-if="!loadingSections.search">
						<div class="flex flex-col gap-3 items-stretch sm:items-center sm:flex-row">
							<div class="flex-1">
								<InputText v-model="searchQuery" :placeholder="$t('tracking.searchTasks')"
									class="w-full text-sm sm:text-base" icon="pi pi-search" />
							</div>
							<div class="w-full sm:w-auto">
								<Select v-model="sortMethod" :options="sortOptions" optionLabel="label"
									optionValue="value" :placeholder="$t('tracking.sortByLabel')"
									class="w-full sm:w-48 text-sm sm:text-base" />
							</div>
							<div v-if="searchQuery"
								class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
								{{ t('tracking.showingFiltered', {
									count: availableTasks.length, total:
										choreTiles.length
								}) }}
							</div>
						</div>
					</template>
					<div v-else class="flex gap-3">
						<Skeleton width="100%" height="2.5rem" class="flex-1" />
						<Skeleton width="12rem" height="2.5rem" />
					</div>
				</template>
			</Card>

			<!-- One-time Task Creation -->
			<Card v-if="showOneTimeForm" class="mx-2 sm:mx-0">
				<template #title>
					<div class="flex items-center justify-between">
						<h2 class="text-lg sm:text-xl font-semibold">{{ $t('createOneTimeTask') }}</h2>
						<Button icon="pi pi-times" text @click="showOneTimeForm = false" :aria-label="$t('cancel')"
							class="p-1 sm:p-2" />
					</div>
				</template>
				<template #content>
					<OneTimeChoreForm :users="users" @submitted="onOneTimeTaskCreated"
						@cancel="showOneTimeForm = false" />
				</template>
			</Card>

			<section class="px-2 sm:px-0">
				<template v-if="!loadingSections.availableTasks">
					<div class="flex items-center justify-between mb-4">
						<h2 class="text-xl sm:text-2xl font-semibold">
							{{ $t('tracking.availableTasks') }}
						</h2>
					</div>

					<!-- Available Tasks Grid -->
					<div v-if="availableTasks.length > 0"
						class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
						<SimpleChoreCard v-for="chore in availableTasks" :key="chore.choreId!" :chore="chore"
							:selected="selectedChores.includes(chore.choreId!)" :available-users="users" allow-selection
							allow-edit @select="toggleChoreSelection" />
					</div>

					<!-- Empty State - No Search Results -->
					<Card v-else-if="hasNoSearchResults" class="text-center">
						<template #content>
							<div class="py-6 sm:py-8">
								<i class="pi pi-search text-3xl sm:text-4xl text-gray-400 mb-4"></i>
								<h3 class="text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
									{{ $t('tracking.emptyState.noResults.title') }}
								</h3>
								<p class="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-4">
									{{ $t('tracking.emptyState.noResults.description') }}
								</p>
								<Button :label="$t('cancel')" icon="pi pi-times" text @click="searchQuery = ''"
									class="text-sm" />
							</div>
						</template>
					</Card>

					<!-- Empty State - All Complete -->
					<Card v-else class="text-center">
						<template #content>
							<div class="py-6 sm:py-8">
								<i class="pi pi-check-circle text-3xl sm:text-4xl text-green-500 mb-4"></i>
								<h3 class="text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
									{{ $t('tracking.emptyState.allComplete.title') }}
								</h3>
								<p class="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-4">
									{{ $t('tracking.emptyState.allComplete.description') }}
								</p>
								<Button :label="$t('tracking.emptyState.allComplete.action')" icon="pi pi-plus"
									severity="success" @click="showOneTimeForm = true" class="text-sm" />
							</div>
						</template>
					</Card>
				</template>
				<template v-else>
					<h2 class="text-xl sm:text-2xl font-semibold mb-4">
						{{ $t('tracking.availableTasks') }}
					</h2>
					<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
						<Skeleton v-for="i in 6" :key="i" width="100%" height="12rem" />
					</div>
				</template>
			</section>

			<!-- Selected Tasks Summary & Actions -->
			<Card v-if="selectedChores.length > 0"
				class="sticky bottom-20 md:bottom-4 border-1 border-primary shadow-lg mx-2 sm:mx-0">
				<template #content>
					<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
						<div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
							<div class="flex items-center gap-2">
								<i class="pi pi-check-circle text-primary"></i>
								<span class="font-medium text-sm sm:text-base">
									{{ $t('tracking.tasksSelected', { count: selectedChores.length }) }}
								</span>
							</div>
							<div class="text-sm text-gray-600 dark:text-gray-400">
								{{ $t('tracking.totalXp', { xp: totalSelectedXp }) }}
							</div>
						</div>
						<div class="flex flex-col sm:flex-row gap-2 sm:gap-2">
							<Button :label="$t('cancel')" text @click="clearSelection"
								class="w-full sm:w-auto text-xs sm:text-sm" />
							<Button :label="$t('tracking.completeSelected')" icon="pi pi-check"
								@click="showCompleteDialog = true" class="w-full sm:w-auto text-xs sm:text-sm" />
						</div>
					</div>
				</template>
			</Card>
			<div class="px-2 sm:px-0">
				<template v-if="!loadingSections.todaysChores">
					<TodaysChoresSection :loading="historyStore.loading" :todays-chores="todaysChores"
						:current-user-id="user?.id || undefined" :deleting="historyStore.deleting"
						@delete-chore="handleDeleteChore" />
				</template>
				<template v-else>
					<Card>
						<template #title>
							<Skeleton width="12rem" height="1.5rem" />
						</template>
						<template #content>
							<div class="space-y-3">
								<Skeleton v-for="i in 3" :key="i" width="100%" height="4rem" />
							</div>
						</template>
					</Card>
				</template>
			</div>
		</template>
	</div>

	<!-- Complete Tasks Dialog -->
	<Dialog v-model:visible="showCompleteDialog" :header="$t('tracking.completeTasks')" modal
		class="w-full max-w-md mx-4 sm:mx-auto" :breakpoints="{ '960px': '75vw', '640px': '95vw' }">
		<div class="space-y-4">
			<div>
				<p class="mb-3 text-sm sm:text-base">{{ $t('tracking.confirmCompleteTasks', {
					count:
						selectedChores.length
				}) }}</p>
				<div class="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
					<div class="text-sm space-y-1 max-h-32 overflow-y-auto">
						<div v-for="choreId in selectedChores" :key="choreId" class="truncate">
							{{ getChoreById(choreId)?.name }}
						</div>
					</div>
				</div>
			</div>

			<FloatLabel fluid variant="in">
				<DatePicker v-model="completionDate" inputId="selectedDate" fluid showIcon dateFormat="dd.mm.yy"
					:manualInput="false" showButtonBar />
				<label for="selectedDate">{{ $t("performedAt") }}</label>
			</FloatLabel>
			<FloatLabel fluid variant="in">
				<MultiSelect v-model="selectedParticipants" :options="filteredAndSortedUsers" filter
					optionLabel="username" option-value="id" fluid showClear id="selectedUserIds" />
				<label for="selectedUserIds">{{ $t("performedTogetherWith") }}</label>
			</FloatLabel>
		</div>

		<template #footer>
			<div class="flex flex-col sm:flex-row gap-2 sm:gap-0 justify-end">
				<Button :label="$t('cancel')" text @click="showCompleteDialog = false" class="w-full sm:w-auto" />
				<Button :label="$t('tracking.complete')" @click="completeTasks" :loading="completingTasks"
					:disabled="completionDate == null || !selectedChores.length" class="w-full sm:w-auto" />
			</div>
		</template>
	</Dialog>

	<!-- Schedule Task Modal -->
	<ScheduleTaskModal :showModal="showScheduleModal" :existing-due-chore-ids="dueTasksIds"
		@update:showModal="showScheduleModal = false" />

	<!-- Performance Summary Drawer -->
	<PerformedChoreSummaryDrawer />
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease;
	max-height: 2000px;
	overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
	max-height: 0;
	opacity: 0;
}
</style>

<script setup lang="ts">
import { useTaskBoardStore, useUsersStore, useHistoryStore, usePrizeSettingsStore, usePrizesStore } from "@/stores"
import { computed, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { ChoreInstanceStatus, type ChoreTileDto, type CreatePerformedChoreDto, type CreateAndPerformOneTimeChoreDto, type PerformedChoreDto } from "@/client";
import { useI18n } from "vue-i18n";

import PerformedChoreSummaryDrawer from "@/components/tracking/PerformedChoreSummaryDrawer.vue";
import SimpleChoreCard from "@/components/tracking/SimpleChoreCard.vue";
import StatisticsSection from "@/components/tracking/StatisticsSection.vue";
import OneTimeChoreForm from "@/components/tracking/OneTimeChoreForm.vue";
import ScheduleTaskModal from "@/components/tracking/ScheduleTaskModal.vue";
import PrizeMissingSelectionsAlert from "@/components/prizes/PrizeMissingSelectionsAlert.vue";
import TodaysChoresSection from "@/components/tracking/TodaysChoresSection.vue";
import { useToast } from "primevue/usetoast";
import { useSentry } from "@/composables/sentry";

const toast = useToast();

const { t } = useI18n();

const { ensureChoreTilesLoaded, performChores } = useTaskBoardStore();
const { user, users } = storeToRefs(useUsersStore());
const { choreTiles, performedChoreSummary } = storeToRefs(useTaskBoardStore());
const historyStore = useHistoryStore();
const { performedChores } = storeToRefs(historyStore);
const { ensurePrizeSettingsDataLoaded } = usePrizeSettingsStore();
const { ensurePrizesDataLoaded } = usePrizesStore();

// Reactive state
const loading = ref(true);
const loadingSections = ref({
	prizeAlert: true,
	quickActions: true,
	search: true,
	statistics: true,
	availableTasks: true,
	todaysChores: true
});
const showOneTimeForm = ref(false);
const showScheduleModal = ref(false);
const showCompleteDialog = ref(false);
const completingTasks = ref(false);
const searchQuery = ref("");
const sortMethod = ref<'name-asc' | 'name-desc' | 'points-asc' | 'points-desc' | 'due-date' | 'performed-count-asc' | 'performed-count-desc'>('name-asc');
const selectedChores = ref<string[]>([]);
const completionDate = ref(new Date());
const selectedParticipants = ref<string[]>([]);
const showStatistics = ref(false);

// Helper function to sort chores based on the selected sort method
const sortChores = (chores: ChoreTileDto[]): ChoreTileDto[] => {
	return [...chores].sort((a, b) => {
		switch (sortMethod.value) {
			case 'name-asc':
				return a.name!.localeCompare(b.name!);
			case 'name-desc':
				return b.name!.localeCompare(a.name!);
			case 'points-asc':
				return (a.baseXp! + a.bonusXp!) - (b.baseXp! + b.bonusXp!);
			case 'points-desc':
				return (b.baseXp! + b.bonusXp!) - (a.baseXp! + a.bonusXp!);
			case 'performed-count-asc':
				return (a.performedCount || 0) - (b.performedCount || 0);
			case 'performed-count-desc':
				return (b.performedCount || 0) - (a.performedCount || 0);
			case 'due-date':
			default:
				// Sort by due date first (overdue first), then by name
				if (a.dueAt && !b.dueAt) return -1;
				if (!a.dueAt && b.dueAt) return 1;
				if (a.dueAt && b.dueAt) {
					const dateCompare = new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
					if (dateCompare !== 0) return dateCompare;
				}
				return a.name!.localeCompare(b.name!);
		}
	});
};

// Computed properties
const username = computed(() => user.value?.username);
const filteredAndSortedUsers = computed(() => {
	return users.value
		.filter(u => u.id! != user.value?.id!)
		.sort((a, b) => a.username!.localeCompare(b.username!));
});

// Filter chores only once
const filteredChoreTiles = computed(() => {
	let filtered = choreTiles.value.filter(chore => !chore.isOneTimeTask || chore.choreInstanceId);

	// Apply search filter to all chores
	if (searchQuery.value.trim()) {
		const query = searchQuery.value.toLowerCase().trim();
		filtered = filtered.filter(chore =>
			chore.name!.toLowerCase().includes(query) ||
			chore.description?.toLowerCase().includes(query)
		);
	}
	return filtered;
});

// Separate the filtered chores into due and available, then sort them
const dueTasksIds = computed(() => {
	const dues = filteredChoreTiles.value.filter(chore => chore.status === ChoreInstanceStatus.PENDING).map(chore => chore.choreId!);
	return dues;
});

const availableTasks = computed(() => {
	// const available = filteredChoreTiles.value.filter(chore => chore.status !== ChoreInstanceStatusObject.Pending);
	return sortChores(filteredChoreTiles.value);
});

const totalSelectedXp = computed(() => {
	return selectedChores.value.reduce((total, choreId) => {
		const chore = getChoreById(choreId);
		return total + (chore ? (chore.baseXp! + chore.bonusXp!) : 0);
	}, 0);
});

// Get today's performed chores
const todaysChores = computed(() => {
	const today = new Date();
	const todayStr = today.toDateString();

	return performedChores.value.filter((chore: PerformedChoreDto) => {
		if (!chore.performedAt) return false;
		const choreDate = new Date(chore.performedAt);
		return choreDate.toDateString() === todayStr;
	}).sort((a: PerformedChoreDto, b: PerformedChoreDto) => {
		// Sort by performed time, newest first
		if (!a.performedAt || !b.performedAt) return 0;
		return new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime();
	});
});

// Calculate today's stats for mini-stats display
const todaysStats = computed(() => {
	const totalXp = todaysChores.value.reduce((sum, chore) => {
		// Calculate XP from participants (similar to how history store does it)
		const choreXp = chore.participants?.reduce((choreTotal, participant) => {
			return choreTotal + (participant.totalXpAwarded || 0);
		}, 0) || 0;
		return sum + choreXp;
	}, 0);
	const choreCount = todaysChores.value.length;
	return { totalXp, choreCount };
});

// Check if no results due to search
const hasNoSearchResults = computed(() => {
	return searchQuery.value.trim() !== '' && availableTasks.value.length === 0;
});

const sortOptions = ref([
	{ label: computed(() => t('tracking.sortBy.dueDate')), value: 'due-date' },
	{ label: computed(() => t('tracking.sortBy.performedCountAsc')), value: 'performed-count-asc' },
	{ label: computed(() => t('tracking.sortBy.performedCountDesc')), value: 'performed-count-desc' },
	{ label: computed(() => t('tracking.sortBy.nameAsc')), value: 'name-asc' },
	{ label: computed(() => t('tracking.sortBy.nameDesc')), value: 'name-desc' },
	{ label: computed(() => t('tracking.sortBy.pointsAsc')), value: 'points-asc' },
	{ label: computed(() => t('tracking.sortBy.pointsDesc')), value: 'points-desc' },
]);

// Methods
const getChoreById = (choreId: string): ChoreTileDto | undefined => {
	return choreTiles.value.find(chore => chore.choreId === choreId);
};

const toggleChoreSelection = (chore: ChoreTileDto, selected: boolean) => {
	const choreId = chore.choreId!;
	if (selected) {
		if (!selectedChores.value.includes(choreId)) {
			selectedChores.value.push(choreId);
		}
	} else {
		const index = selectedChores.value.indexOf(choreId);
		if (index > -1) {
			selectedChores.value.splice(index, 1);
		}
	}
};

const clearSelection = () => {
	selectedChores.value = [];
};

const onOneTimeTaskCreated = () => {
	showOneTimeForm.value = false;
};

const completeTasks = async () => {
	completingTasks.value = true;
	try {
		const performedChoreData: CreatePerformedChoreDto[] = selectedChores.value
			.map(choreId => getChoreById(choreId))
			.filter(chore => chore && chore.choreId)
			.map(chore => ({
				choreId: chore!.choreId!,
				performedAt: completionDate.value
			}));

		const oneTimeChoreData: CreateAndPerformOneTimeChoreDto[] = selectedChores.value
			.map(choreId => getChoreById(choreId))
			.filter(chore => chore && !chore.choreId)
			.map(chore => ({
				name: chore!.name!,
				description: chore!.description,
				estimatedMinutes: chore!.estimatedMinutes!,
				categoryId: chore!.categoryId, // You might need to get this from the chore
				difficultyLevel: chore!.difficultyLevel, // You might need to get this from the chore
				performedAt: completionDate.value
			}));
		const performedChoresInfo = await performChores(performedChoreData, oneTimeChoreData, selectedParticipants.value, completionDate.value);

		// Reset form
		console.log("Performed chores info:", performedChoresInfo);
		toast.add({
			severity: 'success',
			summary: t('taskSubmitted'),
			detail: t('taskCompleted', selectedChores.value.length),
			life: 5000
		});
		performedChoreSummary.value = performedChoresInfo;
		clearSelection();
		showCompleteDialog.value = false;
		completionDate.value = new Date();
		selectedParticipants.value = [];

		// Refresh today's chores after completing tasks
		await fetchTodaysChores();
	} catch (error) {
		console.error('Error completing tasks:', error);
		toast.add({
			severity: 'error',
			summary: t('error'),
			detail: t('tracking.errorCompletingTasks'),
			life: 7000
		});
		useSentry().captureException(error as Error);
	} finally {
		completingTasks.value = false;
	}
};

const handleDeleteChore = async (choreId: string) => {
	try {
		await historyStore.deletePerformedChore(choreId);
		toast.add({
			severity: 'success',
			summary: t('common.success'),
			detail: t('history.delete.success'),
			life: 3000
		});
	} catch (error) {
		console.error('Error deleting chore:', error);
		toast.add({
			severity: 'error',
			summary: t('error'),
			detail: t('history.delete.error'),
			life: 5000
		});
		useSentry().captureException(error as Error);
	}
};

const fetchTodaysChores = async () => {
	const today = new Date();
	const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

	try {
		await historyStore.fetchPerformedChores(startOfDay, endOfDay);
	} catch (error) {
		console.error('Error fetching today\'s chores:', error);
	}
};

// Lifecycle
onMounted(async () => {
	console.log("Loading tracking data");

	// Load different sections independently for better UX
	try {
		// Load chore tiles (affects due and available tasks)
		const choreTilesPromise = ensureChoreTilesLoaded().then(() => {
			loadingSections.value.availableTasks = false;
			loadingSections.value.search = false;
			loadingSections.value.quickActions = false;
		});

		// Load today's chores
		const todaysChoresPromise = fetchTodaysChores().then(() => {
			loadingSections.value.todaysChores = false;
		});

		// Load prize settings
		const prizePromises = Promise.all([
			ensurePrizeSettingsDataLoaded(),
			ensurePrizesDataLoaded()
		]).then(() => {
			loadingSections.value.prizeAlert = false;
		});

		// Wait for all to complete
		await Promise.all([choreTilesPromise, todaysChoresPromise, prizePromises]);

		loadingSections.value.statistics = false;
	} catch (error) {
		console.error("Error loading tracking data:", error);
	} finally {
		loading.value = false;
	}
});
</script>